import { ITokenResponse } from "./@types/ResponseTypes";
import { Auth0Client } from "./rest/Auth0Client";
import * as authClient from "../utils/auth0Client";
import jwtDecode from "jwt-decode";

export class TokenProvider {
  private readonly anonTokenKey = "anonToken";
  private readonly anonTokenExpiryKey = "anonTokenExpiry";
  private readonly userTokenKey = "auth-token";

  // This will act as a lock for all token requests
  private anonTokenPromise: Promise<string> | null = null;

  private getFromStorage(key: string): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  }

  private setToStorage(key: string, value: string): void {
    if (typeof window !== "undefined") {
      localStorage.setItem(key, value);
    }
  }

  private checkTokenExpired(): boolean {
    const expiry = Number(this.getFromStorage(this.anonTokenExpiryKey));
    if (!expiry) {
      return true; // Can't read expiry → treat as expired
    }
    const currentTime = Date.now();
    return expiry <= currentTime;
  }

  private async getAuth0MachineToken(): Promise<string> {
    const client = new Auth0Client({});
    const token: ITokenResponse = await client.getMachineToken();

    // token should have accessToken property
    const accessToken: string = token.accessToken || "";
    if (!accessToken) throw new Error("No access token returned from Gapi");
    // Decode token to get expiry
    const decoded: { exp: number } = jwtDecode(accessToken);
    if (!decoded?.exp) {
      throw new Error("Invalid token: no expiry found");
    }
    const expiryTime = decoded.exp * 1000;
    this.setToStorage(this.anonTokenExpiryKey, expiryTime.toString());
    this.setToStorage(this.anonTokenKey, accessToken);
    return accessToken;
  }

  public async getAnonToken(): Promise<string> {
    const cachedToken = this.getFromStorage(this.anonTokenKey);

    if (cachedToken && !this.checkTokenExpired()) {
      return cachedToken;
    }

    // If token fetch already in progress, return the same promise
    if (this.anonTokenPromise) {
      return this.anonTokenPromise;
    }

    // Lock the token fetch
    this.anonTokenPromise = this.getAuth0MachineToken();

    try {
      const newToken = await this.anonTokenPromise;
      return newToken;
    } finally {
      // Always reset the promise once done to allow next round
      this.anonTokenPromise = null;
    }
  }

  public async getUserToken(): Promise<string> {
    return authClient.getToken();
  }
}

export const TokenProviderInstance = new TokenProvider();
